import React from 'react'
import { Box, Typography } from '@mui/material'
import { TrendingUp, Zap, Calendar, Star } from 'lucide-react'

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

function getEngagementColor(rate: number): string {
  if (rate >= 3) return '#22c55e'
  if (rate >= 1) return '#eab308'
  return '#ef4444'
}

function getEngagementLabel(rate: number): string {
  if (rate >= 6) return 'Exceptional'
  if (rate >= 3) return 'Great'
  if (rate >= 1) return 'Average'
  return 'Below Average'
}

interface Props {
  data: {
    engagementRate: number
    bestPost: {
      codeUrl: string | null
      caption: string | null
      likeCount: number
      commentCount: number
      totalEngagement: number
      createdAt: string | null
    } | null
    postingFrequency: number
    totalPostsAnalyzed: number
  }
}

export default function FreeReport({ data }: Props): JSX.Element {
  const engColor = getEngagementColor(data.engagementRate)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
      {/* Stats grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {/* Engagement rate */}
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TrendingUp size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Engagement Rate
            </Typography>
          </Box>
          <Typography sx={{ color: engColor, fontWeight: 800, fontSize: '32px', lineHeight: 1 }}>
            {data.engagementRate}%
          </Typography>
          <Typography sx={{ color: engColor, fontSize: '12px', mt: 0.5 }}>
            {getEngagementLabel(data.engagementRate)}
          </Typography>
        </Box>

        {/* Posting frequency */}
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Calendar size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Posting Frequency
            </Typography>
          </Box>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '32px', lineHeight: 1 }}>
            {data.postingFrequency}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', mt: 0.5 }}>
            posts per week
          </Typography>
        </Box>
      </Box>

      {/* Best post */}
      {data.bestPost && (
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Star size={16} color="#eab308" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Best Performing Post
            </Typography>
          </Box>
          {data.bestPost.caption && (
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.5, mb: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {data.bestPost.caption}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>{formatNumber(data.bestPost.likeCount)}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Likes</Typography>
            </Box>
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>{formatNumber(data.bestPost.commentCount)}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Comments</Typography>
            </Box>
            <Box>
              <Typography sx={{ color: '#a855f7', fontWeight: 700, fontSize: '16px' }}>{formatNumber(data.bestPost.totalEngagement)}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Total</Typography>
            </Box>
          </Box>
          {data.bestPost.codeUrl && (
            <Typography
              component="a"
              href={data.bestPost.codeUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: '#a855f7', fontSize: '12px', mt: 1.5, display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              View post on Instagram
            </Typography>
          )}
        </Box>
      )}

      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', textAlign: 'center' }}>
        Based on {data.totalPostsAnalyzed} recent posts
      </Typography>
    </Box>
  )
}
